import React, {forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import 'styles/chat/Keyboard.css';
import {MarketContext} from 'util/market/MarketContext';
import {useSessionConfiguration} from 'util/SessionConfigurationContext';

const KEYBOARD_KEYS = {
    normal: [
        '`1234567890-='.split(''),
        'qwertyuiop[]\\'.split(''),
        'asdfghjkl;\''.split(''),
        'zxcvbnm,./'.split(''),
    ],
    shift: [
        '~!@#$%^&*()_+'.split(''),
        'QWERTYUIOP{}|'.split(''),
        'ASDFGHJKL:"'.split(''),
        'ZXCVBNM<>?'.split(''),
    ],
};

const isLetterRegex = /^[a-zA-Z]$/;

const UniversalKeyboard = forwardRef(({onKeyPress, isPictureComment, highlightedKey}, ref) => {
    const {allowedLetters} = useContext(MarketContext);
    const [pressedKeys, setPressedKeys] = useState([]);
    const [isShiftPressed, setIsShiftPressed] = useState(false);
    const isShiftPressedRef = useRef(false);
    const [isCapsLockOn, setIsCapsLockOn] = useState(false);
    const isCapsLockOnRef = useRef(false);
    const [activeRows, setActiveRows] = useState(KEYBOARD_KEYS.normal);
    const [mousePressed, setMousePressed] = useState(null);
    const {isDesktopView, isTouch} = useSessionConfiguration();

    useImperativeHandle(ref, () => ({
        realKeyDown: (key) => {
            if (!pressedKeys.includes(key)) {
                setPressedKeys((prevKeys) => [...prevKeys, key]);
            }
        },
        realKeyUp: (key) => {
            setPressedKeys((prevKeys) => prevKeys.filter((pressedKey) => pressedKey !== key));
        },
    }));

    useEffect(() => {
        const checkCapsLockState = (event) => {
            const isOn = event.getModifierState('CapsLock');
            setIsCapsLockOn(isOn);
            isCapsLockOnRef.current = isOn;
        };

        const tempListener = (event) => {
            checkCapsLockState(event);
            window.removeEventListener('keydown', tempListener);
        };

        window.addEventListener('keydown', tempListener);

        const handleMouseUp = () => {
            setMousePressed(null);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Shift') {
                setIsShiftPressed(true);
                if (!isShiftPressedRef.current) {
                    setPressedKeys([]);
                }
                isShiftPressedRef.current = true;
            }
            if (event.getModifierState('CapsLock')) {
                if (!isCapsLockOnRef.current) {
                    setPressedKeys([]);
                }
                setIsCapsLockOn(true);
                isCapsLockOnRef.current = true;
            }
        };

        const handleKeyUp = (event) => {
            setPressedKeys((prevKeys) => prevKeys.filter((key) => key !== event.key));
            if (event.key === 'Shift') {
                setIsShiftPressed(false);
                if (isShiftPressedRef.current) {
                    setPressedKeys([]);
                }
                isShiftPressedRef.current = false;
            }
            if (!event.getModifierState('CapsLock')) {
                setIsCapsLockOn(false);
                if (isCapsLockOnRef.current) {
                    setPressedKeys([]);
                }
                isCapsLockOnRef.current = false;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('dragend', handleMouseUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('keydown', tempListener);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('dragend', handleMouseUp);
        };
    }, []);

    useEffect(() => {
        const result = isShiftPressed ? KEYBOARD_KEYS.shift : KEYBOARD_KEYS.normal;
        setActiveRows(result);
    }, [isShiftPressed]);

    const getDisplayedLetter = (letter) => {
        if (isCapsLockOn && isShiftPressed) {
            return letter.toLowerCase();
        }
        if (isCapsLockOn || isShiftPressed) {
            return letter.toUpperCase();
        }
        return letter;
    };

    const mouseDownHandler = (event, letter, isDesktop) => {
        if (event.cancelable) {
            event.preventDefault();
        }
        if ((isDesktop && isTouch) || (!isDesktop && !isTouch)) {
            return;
        }
        setMousePressed(letter);
        onKeyPress(letter);
    };

    const invertShift = () => {
        const originalShift = isShiftPressed;
        setIsShiftPressed(!originalShift);
        isShiftPressedRef.current = !originalShift;
        setPressedKeys([]);
    };

    return (
        <div className={'keyboard'}>
            {activeRows.map((row, rowIndex) => (
                <div key={rowIndex} className={`keyboard-row ${isDesktopView ? '' : 'mobile'}`}>
                    {row.map((letter, index) => {
                        const displayedLetter = getDisplayedLetter(letter);
                        const classNames = ['keyboard-key'];

                        if (!allowedLetters.includes(displayedLetter)) {
                            classNames.push('disabled');
                        } else {
                            classNames.push('enabled');
                        }

                        if (isLetterRegex.test(displayedLetter)) {
                            classNames.push('letter');
                        }

                        const lowerCaseLetter = displayedLetter.toLowerCase();
                        const upperCaseLetter = displayedLetter.toUpperCase();
                        const isPressed =
                            pressedKeys.includes(lowerCaseLetter) ||
                            pressedKeys.includes(upperCaseLetter) ||
                            mousePressed === upperCaseLetter ||
                            mousePressed === lowerCaseLetter ||
                            highlightedKey === upperCaseLetter ||
                            highlightedKey === lowerCaseLetter;
                        if (displayedLetter === 'A') {
                            console.log('Is pressed:', isPressed);
                            if (!isPressed) {
                                console.log('Pressed keys:', pressedKeys);
                            }
                        }
                        if (isPressed) {
                            classNames.push('pressed');
                        }
                        return (
                            <div
                                key={index}
                                className={'keyboard-key-wrapper'}
                                onMouseDown={event => mouseDownHandler(event, displayedLetter, true)}
                                onTouchStart={event => mouseDownHandler(event, displayedLetter, false)}
                            >
                                <div className={classNames.join(' ')}>
                                    {displayedLetter}
                                </div>
                            </div>
                        );
                    })}
                    {rowIndex === 2 && (
                        <div style={{width: '50px'}}/>
                    )}
                    {rowIndex === 3 && (
                        <div style={{width: '70px'}}/>
                    )}
                </div>
            ))}
            <div className="keyboard-row">
                <div
                    className="keyboard-key shift"
                    onClick={invertShift}
                >
                    {isShiftPressed ? 'SHIFT' : 'Shift'}
                </div>
                <div
                    className={
                        `keyboard-key 
                        spacebar 
                        ${allowedLetters.includes(' ') ? 'enabled' : 'disabled'} 
                        ${(pressedKeys.includes(' ') || mousePressed === ' ') ? 'pressed' : ''} 
                        ${(highlightedKey === ' ') ? 'highlighted' : ''}`
                    }
                    onMouseDown={event => mouseDownHandler(event, ' ', true)}
                    onTouchStart={event => mouseDownHandler(event, ' ', false)}
                >
                    Space
                </div>
                <div style={{width: '60px'}}/>
            </div>
        </div>
    );
});

export default UniversalKeyboard;
